if (!customElements.get("variant-downloadable")) {
  customElements.define(
    "variant-downloadable",
    class VariantDownloadable extends HTMLElement {
      connectedCallback() {
        if (!this.initialized) {
          this.initialized = true;
          this.details = this.querySelector("details");
          this.links = [...this.querySelectorAll("[data-downloadable-link]")];

          try {
            this.variantUrls = JSON.parse(
              this.querySelector("[data-downloadable-variants]")?.textContent || "{}",
            );
          } catch {
            this.variantUrls = {};
          }

          this.handleVariantChanged = (event) => {
            const eventSectionId = event.detail?.sectionId;
            if (eventSectionId && this.dataset.sectionId && eventSectionId !== this.dataset.sectionId) return;

            const variantId = event.detail?.variant?.id;
            if (variantId == null) return;
            this.dataset.variantId = variantId;
            this.update(variantId);
          };
        }

        document.removeEventListener("variant:changed", this.handleVariantChanged);
        document.addEventListener("variant:changed", this.handleVariantChanged);
        this.update(this.dataset.variantId);
      }

      disconnectedCallback() {
        document.removeEventListener("variant:changed", this.handleVariantChanged);
      }

      update(variantId) {
        const urls = this.variantUrls?.[String(variantId)] || {};
        let availableLinks = 0;

        this.links.forEach((link) => {
          const url = urls[link.dataset.downloadableType] || "";
          const unavailable = !url;

          link.toggleAttribute("hidden", unavailable);
          if (unavailable) link.removeAttribute("href");
          else {
            link.href = url;
            availableLinks += 1;
          }
        });

        this.details?.toggleAttribute("hidden", availableLinks === 0);
      }
    },
  );
}

if (!customElements.get("inventory-levels")) {
  customElements.define(
    "inventory-levels",
    class InventoryLevels extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.details = this.closest("details");
        this.list = this.querySelector("[data-inventory-list]");
        this.sectionId = this.closest("[data-section]")?.dataset.section || "";
        this.details?.addEventListener("toggle", () => this.load());

        this.handleVariantChanged = (event) => {
          const eventSectionId = event.detail?.sectionId;
          if (eventSectionId && this.sectionId && eventSectionId !== this.sectionId) return;

          const sku = event.detail?.variant?.sku;
          if (sku === undefined) return;

          this.requestController?.abort();
          this.loading = false;
          this.dataset.sku = sku || "";
          this.loaded = false;
          this.cachedSku = null;
          this.cachedWarehouses = null;

          if (this.details?.open && this.list) {
            this.list.setAttribute("aria-busy", "true");
            this.list.innerHTML = '<p class="pdp-commercial-details__empty">Loading…</p>';
          }

          const summaryStock = this.closest("details")?.querySelector(".pdp-commercial-details__stock");
          const available = event.detail?.variant?.available;
          this.dataset.variantAvailable = available ? "true" : "false";
          if (summaryStock) {
            summaryStock.classList.toggle("is-unavailable", !available);
            summaryStock.innerHTML = `<i></i>${available ? "In Stock" : "Out of Stock"}`;
          }

          if (!sku && this.list) {
            this.list.innerHTML = '<p class="pdp-commercial-details__empty">Warehouse availability is unavailable for this product.</p>';
            return;
          }

          this.fetchInventory(this.details?.open);
        };
        document.addEventListener("variant:changed", this.handleVariantChanged);

        this.fetchInventory();
      }

      disconnectedCallback() {
        document.removeEventListener("variant:changed", this.handleVariantChanged);
        this.requestController?.abort();
      }

      async fetchInventory(showList = false) {
        if (this.loading) {
          if (showList && this.list) {
            this.list.setAttribute("aria-busy", "true");
            this.list.innerHTML = '<p class="pdp-commercial-details__empty">Loading…</p>';
          }
          return;
        }
        if (showList && this.loaded) return;

        const sku = this.dataset.sku;
        if (!sku || !this.list) return;

        if (this.cachedSku === sku && this.cachedWarehouses) {
          this.render(this.cachedWarehouses, showList);
          if (showList) this.loaded = true;
          return;
        }

        this.loading = true;
        this.requestController?.abort();
        this.requestController = new AbortController();
        const requestedSku = sku;

        if (showList) {
          this.list.setAttribute("aria-busy", "true");
          this.list.innerHTML = '<p class="pdp-commercial-details__empty">Loading…</p>';
        }

        try {
          const rootUrl = window.Shopify?.routes?.root || "/";
          const response = await fetch(`${rootUrl}apps/ubique/inventory`, {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ sku }),
            credentials: "same-origin",
            cache: "no-store",
            signal: this.requestController.signal,
          });

          if (requestedSku !== this.dataset.sku) return;

          if (response.status === 204) {
            if (showList || this.details?.open) {
              this.list.innerHTML = '<p class="pdp-commercial-details__empty">Warehouse availability is unavailable for this product.</p>';
              this.loaded = true;
            }
            return;
          }

          if (!response.ok) {
            if (showList || this.details?.open) {
              this.list.innerHTML = '<p class="pdp-commercial-details__empty">Warehouse availability is temporarily unavailable.</p>';
            }
            return;
          }

          const contentType = response.headers.get("content-type") || "";
          if (!contentType.includes("application/json")) {
            throw new Error("Inventory endpoint did not return JSON");
          }

          const data = await response.json();
          const warehouses = Array.isArray(data.warehouses) ? data.warehouses : [];
          this.cachedSku = requestedSku;
          this.cachedWarehouses = warehouses;
          const shouldShowList = showList || this.details?.open;
          this.render(warehouses, shouldShowList);
          if (shouldShowList) this.loaded = true;
        } catch (error) {
          if (error.name === "AbortError") return;
          if (showList || this.details?.open) {
            this.list.innerHTML = '<p class="pdp-commercial-details__empty">Warehouse availability is temporarily unavailable.</p>';
          }
        } finally {
          if (requestedSku === this.dataset.sku) {
            this.loading = false;
            if (showList || this.details?.open) this.list.removeAttribute("aria-busy");
          }
        }
      }

      load() {
        if (!this.details?.open) return;
        this.fetchInventory(true);
      }

      render(warehouses, showList = true) {
        const summaryStock = this.closest("details")?.querySelector(".pdp-commercial-details__stock");

        if (!warehouses.length) {
          if (showList) {
            this.list.innerHTML = '<p class="pdp-commercial-details__empty">Warehouse availability is unavailable for this product.</p>';
          }
          return;
        }

        const fragment = document.createDocumentFragment();
        const totalAvailable = warehouses.find((warehouse) => {
          const name = warehouse.warehouseName || warehouse.warehouseId || "";
          return String(name).trim().toLowerCase() === "total available";
        });
        let summedTotal = 0;
        warehouses.forEach((w) => {
          const row = document.createElement("div");
          const name = document.createElement("span");
          const status = document.createElement("strong");
          const indicator = document.createElement("i");
          const qty = w.inventory != null ? Number(w.inventory) : null;

          if (qty != null && w !== totalAvailable) summedTotal += qty;

          row.className = "pdp-inventory-level";
          name.textContent = w.warehouseName || w.warehouseId || "";
          status.className = qty > 0 ? "is-available" : "is-unavailable";
          indicator.setAttribute("aria-hidden", "true");
          status.append(indicator, qty != null ? (qty > 0 ? `In Stock: ${qty}` : "Out of Stock") : "");
          row.append(name, status);
          fragment.append(row);
        });
        if (showList) this.list.replaceChildren(fragment);

        const aggregateQuantity = totalAvailable?.inventory;
        const total = aggregateQuantity != null ? Number(aggregateQuantity) || 0 : summedTotal;

        // Warehouse stock is physical inventory. Shopify variant availability
        // separately controls whether the product can be purchased.
        if (summaryStock) {
          summaryStock.classList.toggle("is-unavailable", total === 0);
          summaryStock.innerHTML = `<i></i>${total > 0 ? `In Stock: ${total}` : "Out of Stock"}`;
        }

        this.updateProductInventory(total);
      }

      get productInventory() {
        return this.closest("[data-section]")?.querySelector("[data-product-inventory-status]");
      }

      updateProductInventory(total) {
        const inventory = this.productInventory;
        if (!inventory) return;

        const status = inventory.querySelector(".product__inventory");
        const text = inventory.querySelector(".product__inventory-text");
        const progress = inventory.querySelector("progress-bar");
        if (!status || !text) return;

        const threshold = Number(inventory.dataset.inventoryThreshold) || 0;
        const showQuantity = inventory.dataset.showInventoryQuantity === "true";
        const isLowStock = total > 0 && total <= threshold;
        // Keep the displayed inventory tied to the warehouse total; purchase
        // availability is handled by the product form via variant.available.
        const state = total <= 0 ? "out-of-stock" : isLowStock ? "low-stock" : "in-stock";
        let message = inventory.dataset.outOfStockLabel;

        if (total > 0 && isLowStock) {
          message = showQuantity
            ? (total === 1 ? inventory.dataset.lowStockOneTemplate : inventory.dataset.lowStockManyTemplate)
            : inventory.dataset.lowStockLabel;
        } else if (total > 0) {
          message = showQuantity ? inventory.dataset.inStockCountTemplate : inventory.dataset.inStockLabel;
        }

        status.classList.remove("product__inventory--in-stock", "product__inventory--low-stock", "product__inventory--out-of-stock");
        status.classList.add(`product__inventory--${state}`);
        text.innerHTML = String(message || "").replace("__COUNT__", String(total));

        if (progress) {
          progress.hidden = !isLowStock || total >= threshold;
          progress.dataset.value = String(total);
          if (threshold > 0) progress.setPercentage?.(total, threshold);
        }
      }
    },
  );
}

async function fetchFreightQuote({ zipcode, sku, quantity }) {
  const rootUrl = window.Shopify?.routes?.root || "/";
  const response = await fetch(`${rootUrl}apps/ubique/freight-estimate`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      zipcode,
      location_type: "commercial",
      items: [{ sku, quantity }],
    }),
    credentials: "same-origin",
  });

  if (response.status === 204) return null;
  if (!response.ok) throw new Error(`Freight estimate failed with ${response.status}`);

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error("Freight estimate endpoint did not return JSON");
  }

  const data = await response.json();
  return data;
}

if (!customElements.get("freight-calculator")) {
  customElements.define(
    "freight-calculator",
    class FreightCalculator extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.form = this.querySelector("form");
        this.result = this.querySelector("[role='status']");
        this.note = this.querySelector("[data-freight-note]");
        this.description = this.parentElement?.querySelector("[data-freight-description]");
        this.submitButton = this.form?.querySelector('[type="submit"]');
        this.productForm = this.dataset.productFormId
          ? document.getElementById(this.dataset.productFormId)
          : null;
        this.quantityInput = this.productForm?.querySelector('[name="quantity"]');
        this.updateDescription = () => {
          const quantity = Math.max(1, parseInt(this.quantityInput?.value || "1", 10) || 1);
          const template = this.description?.dataset.template || "Freight Quote calculated for [quantity] item(s)";
          if (this.description) {
            this.description.textContent = template
              .replaceAll("[quantity]", String(quantity))
              .replace("item(s)", quantity === 1 ? "item" : "items");
          }
        };
        this.quantityInput?.addEventListener("input", this.updateDescription);
        this.quantityInput?.addEventListener("change", this.updateDescription);
        this.updateDescription();
        this.sectionId = this.closest("[data-section]")?.dataset.section || "";
        this.handleVariantChanged = (event) => {
          const eventSectionId = event.detail?.sectionId;
          if (eventSectionId && this.sectionId && eventSectionId !== this.sectionId) return;

          const variant = event.detail?.variant;
          if (!variant) return;
          this.dataset.variantId = variant.id || "";
          this.dataset.sku = variant.sku || "";
        };
        document.addEventListener("variant:changed", this.handleVariantChanged);

        this.form?.addEventListener("submit", async (event) => {
          event.preventDefault();
          const postalCode = new FormData(this.form)
            .get("postal_code")
            ?.toString()
            .trim();
          const quantity = Math.max(1, parseInt(this.quantityInput?.value || "1", 10) || 1);
          const sku = this.dataset.sku?.trim();
          if (!postalCode || !sku || !this.result) return;
          const strings = window.FoxTheme?.freightEstimateStrings || {};

          this.submitButton?.setAttribute("disabled", "");
          this.form.setAttribute("aria-busy", "true");
          this.result.textContent = strings.calculating || "Calculating…";
          this.result.hidden = false;
          if (this.note) this.note.hidden = true;

          try {
            const estimate = await fetchFreightQuote({
              zipcode: postalCode,
              sku,
              quantity,
            });

            if (estimate?.total_price == null || estimate.total_price === "") {
              this.result.textContent = strings.destinationUnavailable || "Freight estimate is unavailable for this destination.";
              return;
            }

            const numericPrice = Number(estimate.total_price);
            const formattedPrice = Number.isFinite(numericPrice)
              ? new Intl.NumberFormat(undefined, {
                  style: "currency",
                  currency: estimate.currency || "USD",
                }).format(numericPrice)
              : String(estimate.total_price);
            const serviceName = estimate.service_name || strings.freightLabel || "Freight";

            this.result.replaceChildren();
            this.result.append(`${serviceName}: `);
            const amount = document.createElement("strong");
            amount.textContent = formattedPrice;
            this.result.append(amount);
            if (this.note) this.note.hidden = false;
          } catch {
            this.result.textContent = strings.unavailable || "Freight estimate is temporarily unavailable.";
            if (this.note) this.note.hidden = true;
          } finally {
            this.submitButton?.removeAttribute("disabled");
            this.form.removeAttribute("aria-busy");
          }
        });
      }

      disconnectedCallback() {
        document.removeEventListener("variant:changed", this.handleVariantChanged);
        this.quantityInput?.removeEventListener("input", this.updateDescription);
        this.quantityInput?.removeEventListener("change", this.updateDescription);
      }
    },
  );
}

function initializePersonalizationFonts(root = document) {
  root.querySelectorAll("[data-product-personalization]").forEach((personalization) => {
    const fontSelect = personalization.querySelector("select");
    if (!fontSelect || fontSelect.dataset.fontPreviewInitialized) return;
    fontSelect.dataset.fontPreviewInitialized = "true";

    const updateFontPreview = () => {
      const selectedOption = fontSelect.options[fontSelect.selectedIndex];
      const fontFamily = selectedOption?.dataset.fontFamily || selectedOption?.value;
      if (!fontFamily) return;
      personalization.style.setProperty(
        "--personalization-font-family",
        `'${fontFamily}', sans-serif`,
      );
    };

    fontSelect.addEventListener("change", updateFontPreview);
    updateFontPreview();
  });
}

function initializePersonalizationColors(root = document) {
  root.querySelectorAll("[data-personalization-show-more]").forEach((button) => {
    if (button.dataset.showMoreInitialized) return;
    button.dataset.showMoreInitialized = "true";

    button.addEventListener("click", () => {
      const colorList = button.closest(".product-personalization__color-list");
      if (!colorList) return;

      colorList.classList.add("is-expanded");
      button.setAttribute("aria-expanded", "true");
    });
  });
}

function initializeProductExtendedToggles(root = document) {
  root
    .querySelectorAll(".product-extended > .accordion-details__summary")
    .forEach((summary) => {
      if (summary.dataset.extendedToggleInitialized) return;
      summary.dataset.extendedToggleInitialized = "true";

      summary.addEventListener(
        "click",
        (event) => {
          const details = summary.closest("details");
          if (!details) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          details.open = !details.open;
        },
        true,
      );
    });
}

initializePersonalizationFonts();
initializePersonalizationColors();
initializeProductExtendedToggles();
document.addEventListener("shopify:section:load", (event) => {
  initializePersonalizationFonts(event.target);
  initializePersonalizationColors(event.target);
  initializeProductExtendedToggles(event.target);
});

function renderProductSpecifications(element) {
  if (!element || element.querySelector(".pdp-spec-table")) return;

  const rows = [];
  const seen = new Set();
  const contentElements = Array.from(element.querySelectorAll("p, li"));
  const sourceLines = contentElements.length
    ? contentElements.map((item) => item.textContent)
    : element.textContent.split(/\n+/);

  sourceLines
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const separator = line.indexOf(":");
      if (separator < 1) return;

      const label = line.slice(0, separator).trim();
      const value = line.slice(separator + 1).trim();
      const key = `${label}\u0000${value}`;
      if (!label || !value || seen.has(key)) return;
      seen.add(key);
      rows.push([label, value]);
    });

  if (!rows.length) return;

  const table = document.createElement("div");
  table.className = "pdp-spec-table";
  rows.forEach(([label, value]) => {
    const row = document.createElement("div");
    const labelElement = document.createElement("span");
    const valueElement = document.createElement("strong");
    labelElement.textContent = label;
    valueElement.textContent = value;
    row.append(labelElement, valueElement);
    table.append(row);
  });
  element.replaceChildren(table);
}

function initializeProductSpecifications(root = document) {
  root.querySelectorAll("product-specifications").forEach(renderProductSpecifications);
}

if (!customElements.get("product-specifications")) {
  customElements.define(
    "product-specifications",
    class ProductSpecifications extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        renderProductSpecifications(this);

        this.detailsId = this.closest("details")?.id || "";
        this.sectionId = this.closest("[data-section]")?.dataset.section || "";
        this.handleVariantChanged = (event) => {
          const eventSectionId = event.detail?.sectionId;
          if (eventSectionId && this.sectionId && eventSectionId !== this.sectionId) return;
          if (!event.detail?.html || !this.detailsId) return;

          const updatedContent = event.detail.html
            .getElementById(this.detailsId)
            ?.querySelector("product-specifications");
          if (!updatedContent) return;

          this.replaceChildren(...Array.from(updatedContent.childNodes, (node) => node.cloneNode(true)));
          renderProductSpecifications(this);
        };
        document.addEventListener("variant:changed", this.handleVariantChanged);
      }

      disconnectedCallback() {
        document.removeEventListener("variant:changed", this.handleVariantChanged);
      }
    },
  );
}

initializeProductSpecifications();
document.addEventListener("shopify:section:load", (event) => {
  initializeProductSpecifications(event.target);
});
